import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Chip, Input, Select, SelectItem, Spinner } from '@heroui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { type FC, useMemo, useState } from 'react';
import { BadgeButton } from '@/primitives/badge.tsx';
import { Text } from '@/primitives/text.tsx';
import type { StaffMemberAvailabilityModel } from '../types/availability-slot.type.ts';
import { ConfirmationStatusEnum } from '../types/confirmation-status.model.ts';
import { getAvailabilityColor } from '../types/constants.ts';
import { NoData } from './no-data.tsx';

interface StaffAvailabilityListProps {
  staffMemberAvailabilities: StaffMemberAvailabilityModel[] | any[];
  accountId?: number;
  isEditable?: boolean;
  onStatusChange?: (staffMemberId: number, status: ConfirmationStatusEnum, index?: number) => void;
  isLoading?: boolean;
  showPendingIndicator?: boolean;
}

type SortOption = 'name' | 'position' | 'status';

const StaffAvailabilityList: FC<StaffAvailabilityListProps> = ({
  staffMemberAvailabilities,
  accountId,
  isEditable = false,
  onStatusChange,
  isLoading = false,
  showPendingIndicator = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');

  const matchesSearchFilter = (
    staff: StaffMemberAvailabilityModel | any,
    search: string,
  ): boolean => {
    if (!search) {
      return true;
    }
    const lowerSearch = search.toLowerCase();
    const firstName = staff.staffMember?.firstName?.toLowerCase() || '';
    const lastName = staff.staffMember?.lastName?.toLowerCase() || '';
    const title = staff.staffMember?.title?.toLowerCase() || '';
    const position =
      staff.staffMember?.accountSiteStaffMembers
        ?.find(x => x.accountId === accountId)
        ?.position?.toLowerCase() || '';

    return (
      firstName.includes(lowerSearch) ||
      lastName.includes(lowerSearch) ||
      title.includes(lowerSearch) ||
      position.includes(lowerSearch) ||
      `${firstName} ${lastName}`.includes(lowerSearch)
    );
  };

  const sortedAndFilteredStaff = useMemo(() => {
    const filtered = staffMemberAvailabilities.filter(staff =>
      matchesSearchFilter(staff, searchTerm),
    );

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name': {
          const aName = `${a.staffMember?.lastName || ''} ${a.staffMember?.firstName || ''}`;
          const bName = `${b.staffMember?.lastName || ''} ${b.staffMember?.firstName || ''}`;
          return aName.localeCompare(bName);
        }
        case 'position': {
          const aPosition =
            a.staffMember?.accountSiteStaffMembers?.find(x => x.accountId === accountId)
              ?.position || '';
          const bPosition =
            b.staffMember?.accountSiteStaffMembers?.find(x => x.accountId === accountId)
              ?.position || '';
          return aPosition.localeCompare(bPosition);
        }
        case 'status': {
          const aStatus = a.confirmationStatus?.description || 'Unconfirmed';
          const bStatus = b.confirmationStatus?.description || 'Unconfirmed';
          return aStatus.localeCompare(bStatus);
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [staffMemberAvailabilities, searchTerm, sortBy, accountId]);

  const hasStaff = staffMemberAvailabilities.length > 0;
  const hasFilteredResults = sortedAndFilteredStaff.length > 0;
  const showEmptySearch = hasStaff && !hasFilteredResults && searchTerm;

  if (isLoading) {
    return (
      <div className='flex justify-center py-8'>
        <Spinner size='lg' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-col sm:flex-row gap-3'>
        <Input
          placeholder='Search staff by name, title, or position...'
          value={searchTerm}
          onValueChange={setSearchTerm}
          variant='bordered'
          size='lg'
          isClearable
          startContent={<MagnifyingGlassIcon className='size-5 text-default-400' />}
          className='flex-1'
        />
        <Select
          label='Sort by'
          placeholder='Select sort option'
          variant='bordered'
          size='sm'
          selectedKeys={new Set([sortBy])}
          onSelectionChange={keys => {
            const selected = Array.from(keys)[0] as SortOption;
            if (selected) {
              setSortBy(selected);
            }
          }}
          className='sm:w-48'
        >
          <SelectItem key='name'>Name</SelectItem>
          <SelectItem key='position'>Position</SelectItem>
          <SelectItem key='status'>Status</SelectItem>
        </Select>
      </div>

      <div className='border border-default-200 rounded-lg max-h-96 min-h-[300px] overflow-y-auto'>
        {!hasStaff ? (
          <div className='p-8'>
            <NoData />
          </div>
        ) : showEmptySearch ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className='p-8'
          >
            <Text className='text-center text-default-400'>No staff members match your search</Text>
          </motion.div>
        ) : (
          <div className='divide-y divide-default-200'>
            <AnimatePresence mode='popLayout'>
              {sortedAndFilteredStaff.map((value, index) => {
                const staffMember = value.staffMember;
                const position =
                  staffMember?.accountSiteStaffMembers?.find(x => x.accountId === accountId)
                    ?.position || '';

                const isAvailable =
                  value.confirmationStatus?.confirmationStatusId ===
                  ConfirmationStatusEnum.Available;
                const isUnconfirmed =
                  !value.confirmationStatus ||
                  value.confirmationStatus?.confirmationStatusId ===
                    ConfirmationStatusEnum.Unconfirmed;
                const isUnavailable =
                  value.confirmationStatus?.confirmationStatusId ===
                  ConfirmationStatusEnum.Unavailable;

                return (
                  <motion.div
                    key={staffMember?.id || index}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{
                      layout: { duration: 0.2 },
                      opacity: { duration: 0.2 },
                      y: { duration: 0.2 },
                    }}
                    className='p-4 hover:bg-default-50 transition-colors'
                  >
                    <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3'>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <div>
                            <Text className='font-medium text-default-900'>
                              {staffMember?.firstName} {staffMember?.lastName}
                            </Text>
                            {staffMember?.title && (
                              <Text className='text-xs text-default-500 mt-0.5'>
                                {staffMember.title}
                              </Text>
                            )}
                          </div>
                          {showPendingIndicator && value.pendingUpdate && (
                            <span className='text-amber-600'>*</span>
                          )}
                        </div>
                        {position ? (
                          <Text className='text-sm text-default-500 mt-1'>{position}</Text>
                        ) : (
                          <Text className='text-sm text-default-400 italic mt-1'>No position</Text>
                        )}
                      </div>

                      <div className='flex items-center gap-2'>
                        {isEditable ? (
                          <>
                            <BadgeButton
                              onClick={() =>
                                onStatusChange?.(
                                  staffMember?.id,
                                  ConfirmationStatusEnum.Unconfirmed,
                                  index,
                                )
                              }
                              color={getAvailabilityColor(ConfirmationStatusEnum.Unconfirmed)}
                              isChecked={isUnconfirmed}
                            >
                              Unconfirmed
                            </BadgeButton>
                            <BadgeButton
                              onClick={() =>
                                onStatusChange?.(
                                  staffMember?.id,
                                  ConfirmationStatusEnum.Available,
                                  index,
                                )
                              }
                              color={getAvailabilityColor(ConfirmationStatusEnum.Available)}
                              isChecked={isAvailable}
                            >
                              Available
                            </BadgeButton>
                            <BadgeButton
                              onClick={() =>
                                onStatusChange?.(
                                  staffMember?.id,
                                  ConfirmationStatusEnum.Unavailable,
                                  index,
                                )
                              }
                              color={getAvailabilityColor(ConfirmationStatusEnum.Unavailable)}
                              isChecked={isUnavailable}
                            >
                              Unavailable
                            </BadgeButton>
                          </>
                        ) : (
                          <Chip
                            color={getAvailabilityColor(
                              value.confirmationStatus?.confirmationStatusId ??
                                ConfirmationStatusEnum.Unconfirmed,
                            )}
                          >
                            {value.confirmationStatus?.description ?? 'Unconfirmed'}
                          </Chip>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffAvailabilityList;
